import * as React from 'react';
import { Hub } from 'aws-amplify/utils';
import { useLocalStorage } from 'react-use';
// Use the same loader as the rest of the app
import Loader from '../technical/Loader/Loader';

import { getEnvConfig } from '../../envConfig';
import { authSignOut, isAuthenticated, getUserInfo, MFAService } from './amplify-service';
import type { LocalApp } from '../../ApplicationContext';
import type { FetchMFAPreferenceOutput, SetUpTOTPOutput } from 'aws-amplify/auth';
import type { MFAPreferenceValue, MFA_METHOD_ID } from './amplify-service';
import { useApplication } from '../../ApplicationContext';
import { saveImpersonatedUserId, getSavedImpersonatedUserId, clearImpersonatedCognitoUsername } from './AuthStorage';
import { hasAppAccess as hasAppAccessGuard, hasAccessToAnyApp } from '../../accessGuards';
import { useUserOrganizationQuery } from '../../generated/graphql';

// User interface (moved from Authn.tsx)
export interface User {
  cognitoUsername: string;
  name: string | null;
  email: string;
  groups: string[];
  organization: {
    id: number;
    name: string;
  } | null;
}

// AuthUserInfo interface (moved from Authn.tsx)
export interface AuthUserInfo {
  sub?: string;
  email?: string;
  name?: string;
  'cognito:groups'?: string[];
  [key: string]: unknown;
}

export const environmentSchema = {
  parse: (value: string) => value as 'localhost-dev' | 'dev' | 'prod' | 'localhost-prod',
};
export type Environment = 'localhost-dev' | 'dev' | 'prod' | 'localhost-prod';

export interface Organization {
  id: number;
  name: string;
}

// Compatible interface with existing UseAuth hook
export interface AuthContextType {
  // Core auth state
  initialized: boolean;
  user: {
    cognitoUsername: string;
    name: string | null;
    email: string;
    groups: string[];
    organization: {
      id: number;
      name: string;
    } | null;
  } | null;
  mfa: string | null; // MFA status: 'NO_MFA', 'TOTP', 'SMS', 'EMAIL', etc.
  originalUserCognitoUsername: string | null;
  error: Error | null;

  // Auth functions
  signIn: () => void;
  signOut: () => Promise<void>;

  // Derived properties for compatibility
  isAdmin: boolean;
  isImpersonating: boolean;
  hasAccess: (group: string) => boolean;
  isImpersonatingAdmin: boolean;
  setIsImpersonatingAdmin: (val: boolean) => void;
  hasAppAccess: (app: LocalApp) => boolean;
  hasAnyAppAccess: () => boolean;
  impersonate: (user: User) => void;
  stopImpersonation: () => void;

  // MFA functions
  refreshMfaStatus: () => Promise<void>;
  getMfaPreference: () => Promise<FetchMFAPreferenceOutput>;
  setMfaPreference: (preference: { [key in MFA_METHOD_ID]?: MFAPreferenceValue }) => Promise<void>;
  setupTOTP: () => Promise<SetUpTOTPOutput>;
  verifyTOTPSetup: (code: string) => Promise<void>;
  disableMfaMethod: (method: MFA_METHOD_ID) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

const IS_IMPERSONATING_ADMIN_KEY = 'genieIsImpersonatingAdmin';

export function NewAuthProvider({ children }: { children: React.ReactNode }) {
  const config = getEnvConfig();
  const { app } = useApplication();

  const [user, setUser] = React.useState<User | null>(null);
  const [originalUser, setOriginalUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [mfaStatus, setMfaStatus] = React.useState<string | null>(null);
  const [isMounting, setIsMounting] = React.useState(false);

  // Use localStorage for isImpersonatingAdmin state (like the original)
  const [isImpersonatingAdminString, setIsImpersonatingAdminString] = useLocalStorage(
    IS_IMPERSONATING_ADMIN_KEY,
    'false',
    { raw: true }
  );

  // Query for current user's organization - only when authenticated
  const { data: organizationData } = useUserOrganizationQuery({
    skip: !originalUser, // Only run when we have an authenticated user
    fetchPolicy: 'cache-first',
  });

  // Load user auth state
  React.useEffect(() => {
    const loadAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [authenticated, userInfo] = await Promise.all([isAuthenticated(), getUserInfo()]);

        if (!authenticated || !userInfo) {
          setUser(null);
          setMfaStatus(null);
          return;
        }

        // Load MFA status for authenticated user
        try {
          const mfaPreference = await MFAService.getMFAPreference();

          if (!mfaPreference?.enabled || mfaPreference.enabled.length === 0) {
            setMfaStatus('NO_MFA');
          } else {
            // Set to the first enabled method, or 'ENABLED' if multiple
            setMfaStatus(mfaPreference.enabled.length === 1 ? mfaPreference.enabled[0] : 'ENABLED');
          }
        } catch (error) {
          console.error('Error fetching MFA status:', error);
          setMfaStatus('NO_MFA'); // Default to no MFA if error
        }

        // Convert authenticated user to compatible format (this is the REAL user)
        const authenticatedUser = {
          cognitoUsername: userInfo.sub || '',
          name: userInfo.name || null,
          email: userInfo.email || '',
          groups: userInfo['cognito:groups'] || [],
          organization: organizationData?.organization || null,
        };

        // Always store the real authenticated user
        setOriginalUser(authenticatedUser);

        // Check if there's an active impersonation
        const impersonatedUserId = getSavedImpersonatedUserId();

        if (impersonatedUserId) {
          // For now, create a basic impersonated user - the real data will be fetched separately
          // This ensures impersonation doesn't break while we load the full user data
          const impersonatedUser = {
            cognitoUsername: impersonatedUserId,
            name: null, // Will be updated when GraphQL data loads
            email: `${impersonatedUserId}@impersonated.user`, // Placeholder
            groups: authenticatedUser.groups, // Use authenticated user's groups as fallback
            organization: authenticatedUser.organization, // Use authenticated user's org as fallback
          };

          // Set the impersonated user as the display user
          setUser(impersonatedUser);
        } else {
          // No impersonation, show the real authenticated user
          setUser(authenticatedUser);
        }
      } catch (err) {
        console.error('❌ NewAuthProvider auth error:', err);
        setError(err as Error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuth();
  }, [organizationData]); // Re-run when organization data changes

  React.useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      setIsMounting(true);
      switch (payload.event) {
        case 'customOAuthState': {
          // Handle OAuth redirect - could check config.appUrl if available
          window.location.href = payload.data;
          setIsMounting(false);
          break;
        }
        default:
          setIsMounting(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  // Separate effect to enhance impersonated user data when GraphQL data becomes available
  React.useEffect(() => {
    const impersonatedUserId = getSavedImpersonatedUserId();

    if (impersonatedUserId && user && user.cognitoUsername === impersonatedUserId) {
      // We're currently impersonating and have GraphQL data - let's enhance the user data
      // But we need the users query for this, which should be handled by the components that need it
      // For now, we'll keep the basic implementation working
    }
  }, [user]); // Run when user changes

  const signIn = React.useCallback(async () => {
    // Redirect to centralized auth app with return URL
    const currentUrl = window.location.href;
    const authUrl = `${config.externalAuthUrl}/signin?prev=${btoa(currentUrl)}`;

    window.location.href = authUrl;
  }, [config]);

  const signOut = React.useCallback(async () => {
    await authSignOut();
    // Redirect to home page after logout
    window.location.href = '/';
  }, []);

  const impersonate = React.useCallback(
    (newUser: User) => {
      if (!originalUser) {
        return;
      }

      // Save the impersonated user ID to cookies
      saveImpersonatedUserId(newUser.cognitoUsername);

      // Clear session storage (like the original implementation)
      sessionStorage.clear();

      // Update the user state to show the impersonated user
      // The originalUser remains unchanged - it represents the real authenticated user
      setUser({
        cognitoUsername: newUser.cognitoUsername,
        name: newUser.name || null,
        email: newUser.email,
        groups: newUser.groups || originalUser.groups, // Fallback to original user groups
        organization: newUser.organization || null,
      });
    },
    [originalUser]
  );

  const stopImpersonation = React.useCallback(() => {
    if (!originalUser) {
      return;
    }

    // Clear the impersonated user ID from cookies
    clearImpersonatedCognitoUsername();

    // Clear the admin impersonation state from localStorage
    setIsImpersonatingAdminString?.('false');

    // Clear session storage (like when starting impersonation)
    sessionStorage.clear();

    // Restore the original user as the display user
    setUser(originalUser);
  }, [originalUser, setIsImpersonatingAdminString]);

  // MFA functions
  const refreshMfaStatus = React.useCallback(async () => {
    try {
      const mfaPreference = await MFAService.getMFAPreference();

      if (!mfaPreference?.enabled || mfaPreference.enabled.length === 0) {
        setMfaStatus('NO_MFA');
      } else {
        setMfaStatus(mfaPreference.enabled.length === 1 ? mfaPreference.enabled[0] : 'ENABLED');
      }
    } catch (error) {
      console.error('Error refreshing MFA status:', error);
      setMfaStatus('NO_MFA');
    }
  }, []);

  const getMfaPreference = React.useCallback(async () => {
    return await MFAService.getMFAPreference();
  }, []);

  const setMfaPreference = React.useCallback(
    async (preference: { [key in MFA_METHOD_ID]?: MFAPreferenceValue }) => {
      const result = await MFAService.setMFAPreference(preference);
      await refreshMfaStatus(); // Refresh the status after setting preference
      return result;
    },
    [refreshMfaStatus]
  );

  const setupTOTP = React.useCallback(async () => {
    return await MFAService.setupTOTP();
  }, []);

  const verifyTOTPSetup = React.useCallback(
    async (code: string) => {
      const result = await MFAService.verifyTOTPSetup(code);
      await refreshMfaStatus(); // Refresh the status after verification
      return result;
    },
    [refreshMfaStatus]
  );

  const disableMfaMethod = React.useCallback(
    async (method: MFA_METHOD_ID) => {
      const result = await MFAService.disableMFAMethod(method);
      await refreshMfaStatus(); // Refresh the status after disabling
      return result;
    },
    [refreshMfaStatus]
  );

  const value = React.useMemo(() => {
    const parsedIsImpersonatingAdmin = isImpersonatingAdminString === 'true';

    // Calculate isImpersonating using the old logic:
    // - There must be a user
    // - There must be an original user (the real authenticated user)
    // - The current user's cognitoUsername must be different from the original user's cognitoUsername
    const isImpersonating = Boolean(user && originalUser && user.cognitoUsername !== originalUser.cognitoUsername);

    // isAdmin logic: true if user has 'genie' group OR if impersonating with admin flag
    const isAdmin = user?.groups.includes('genie') || (isImpersonating && parsedIsImpersonatingAdmin);

    return {
      // Core auth state (compatible with old interface)
      initialized: !isLoading,
      user,
      mfa: mfaStatus,
      // originalUserCognitoUsername should be the REAL user's ID when impersonating
      originalUserCognitoUsername: isImpersonating ? (originalUser?.cognitoUsername ?? null) : null,
      error,

      // Auth functions
      signIn,
      signOut,
      impersonate,
      stopImpersonation,

      // MFA functions
      refreshMfaStatus,
      getMfaPreference,
      setMfaPreference,
      setupTOTP,
      verifyTOTPSetup,
      disableMfaMethod,

      // Derived properties for compatibility
      isAdmin,
      isImpersonating,
      hasAccess: (group: string) => {
        // If admin (real genie user or impersonating with admin flag), grant all access
        if (isAdmin) return true;
        // Otherwise check user's actual groups
        return user?.groups.includes(group) || false;
      },
      isImpersonatingAdmin: parsedIsImpersonatingAdmin,
      setIsImpersonatingAdmin: (val: boolean) => {
        setIsImpersonatingAdminString?.(val.toString());
      },
      hasAppAccess: (appAccess: LocalApp) => {
        if (!user) return false;
        const securityContext = {
          groups: user.groups,
          app,
          isAdmin,
        };
        return hasAppAccessGuard(appAccess)(securityContext);
      },
      hasAnyAppAccess: () => {
        if (!user) return false;
        const securityContext = {
          groups: user.groups,
          app,
          isAdmin,
        };
        return hasAccessToAnyApp(securityContext);
      },
    };
  }, [
    user,
    originalUser,
    isLoading,
    error,
    mfaStatus,
    signIn,
    signOut,
    impersonate,
    stopImpersonation,
    refreshMfaStatus,
    getMfaPreference,
    setMfaPreference,
    setupTOTP,
    verifyTOTPSetup,
    disableMfaMethod,
    isImpersonatingAdminString,
    setIsImpersonatingAdminString,
    app,
  ]);

  if (isMounting) return <></>;

  return <AuthContext.Provider value={value}>{isLoading ? <Loader /> : children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
